import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import { authApi } from "../endpoints";
import type { ApiError } from "../errors";
import { getApiClientConfig } from "../http";
import { qk } from "../query-keys";
import type { LoginRequest, MeOut, RegisterRequest, TokenPair } from "../types";

type QueryOpts<T> = Omit<UseQueryOptions<T, ApiError>, "queryKey" | "queryFn">;

/** Current user + tenant. Enable it only when an access token is present. */
export function useMe(options?: QueryOpts<MeOut>) {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => authApi.me(),
    staleTime: 60_000,
    ...options,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<TokenPair, ApiError, LoginRequest>({
    mutationFn: async (body) => {
      const tokens = await authApi.login(body);
      getApiClientConfig().setAccessToken(tokens.access_token);
      return tokens;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.me }),
        queryClient.invalidateQueries({ queryKey: qk.tenant }),
        queryClient.invalidateQueries({ queryKey: qk.subscription }),
      ]);
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation<TokenPair, ApiError, RegisterRequest>({
    mutationFn: async (body) => {
      const data = await authApi.register(body);
      getApiClientConfig().setAccessToken(data.tokens.access_token);
      return data.tokens;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.me }),
        queryClient.invalidateQueries({ queryKey: qk.tenant }),
        queryClient.invalidateQueries({ queryKey: qk.subscription }),
      ]);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      try {
        await authApi.logout();
      } catch {
        // Best effort: the local session is dropped regardless.
      }
      getApiClientConfig().setAccessToken(null);
    },
    onSettled: () => {
      queryClient.clear();
    },
  });
}
